import { Button, type ButtonProps, Tooltip } from '@sr/ui-library';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  type RoyaltiesRegistrationGate,
  useRoyaltiesRegistrationGate,
} from '../hooks/useRoyaltiesRegistrationGate';
import { RequestMembershipModal } from './RequestMembershipModal';

type RoyaltiesRegistrationActionProps = {
  /** Presentation only, applied in every state so the button never jumps. */
  buttonProps?: Pick<ButtonProps, 'className' | 'size' | 'variant'>;
  label: string;
  to?: string;
};

/**
 * A button that opens a secondary-royalties registration, which only a Current
 * Member may make. Everyone else gets the membership application, or a
 * disabled button that says why, the same as Add Title.
 */
export const RoyaltiesRegistrationAction = ({
  buttonProps,
  label,
  to,
}: RoyaltiesRegistrationActionProps) => {
  const liveGate = useRoyaltiesRegistrationGate();
  // A stale workspace re-checks membership every few minutes; holding the last
  // settled answer meanwhile keeps the button from flickering to loading.
  const settledGate = useRef<RoyaltiesRegistrationGate>();

  if (liveGate.status !== 'pending') settledGate.current = liveGate;
  const gate =
    liveGate.status === 'pending'
      ? (settledGate.current ?? liveGate)
      : liveGate;

  if (gate.status === 'allowed') {
    return to ? (
      <Button asChild {...buttonProps}>
        <Link className="!no-underline" to={to}>
          {label}
        </Link>
      </Button>
    ) : (
      <Button {...buttonProps} disabled>
        {label}
      </Button>
    );
  }

  if (gate.status === 'pending') {
    return (
      <Button {...buttonProps} disabled loading>
        {label}
      </Button>
    );
  }

  if (gate.status === 'apply') {
    return (
      <RequestMembershipModal
        // The modal's own trigger is `sm`; every other state here is `default`.
        triggerButtonProps={{ size: 'default', ...buttonProps }}
        triggerLabel={label}
        workspace={gate.workspace}
      />
    );
  }

  return (
    <Tooltip content={gate.reason}>
      {/* A disabled button emits no pointer events, so the tooltip needs a
          focusable wrapper to hover and tab to. */}
      <span className={buttonProps?.className} tabIndex={0}>
        <Button {...buttonProps} className="w-full" disabled>
          {label}
        </Button>
      </span>
    </Tooltip>
  );
};
